import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { AuthUser } from '@repo/shared-types';
import type { UpdateProfileInput, ChangePasswordInput } from '@repo/validation';
import { fetchApi } from '../lib/api';
import i18n from '../i18n';
import { useAuthStore } from '../stores/authStore';
import type { AuthMeResponse } from './useAuth';

/**
 * Rename the signed-in user. Mirrors the store so the sidebar/header follow,
 * and patches the `['auth','me']` cache so it doesn't overwrite the rename
 * with the stale login-time payload the next time `useAuthMe`'s effect runs
 * (e.g. on the access-token refresh that follows the first 401 after 15 min).
 */
export function useUpdateProfile() {
  const setUser = useAuthStore((s) => s.setUser);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateProfileInput) =>
      fetchApi<{ user: AuthUser }>('/auth/me', {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: ({ user }) => {
      setUser(user);
      queryClient.setQueryData<AuthMeResponse>(['auth', 'me'], (old) => (old ? { ...old, user } : old));
      toast.success(i18n.t('profile.nameUpdated', { ns: 'settings' }));
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

/**
 * Change the password. The caller inspects `error.code` for
 * `INVALID_CURRENT_PASSWORD` to show a field-level message, so no toast is
 * raised here for that case.
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: (input: ChangePasswordInput) =>
      fetchApi<{ message: string }>('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => toast.success(i18n.t('profile.security.changed', { ns: 'settings' })),
  });
}
